import { KpiEvidence } from '../../services/benchmarkService';
import { count } from './BenchmarkVocabulary';

/**
 * How much evidence a figure needs before it is stated as a result.
 *
 * Below this, or whenever the writer itself labels a metric low-confidence, a
 * figure is shown as indicative only. A supplied sample is usually small and
 * curated — dev's first one scored 8 cases and reported a perfect ROC AUC of
 * 1.0, which is what this protects against.
 */
export const INDICATIVE_CASE_THRESHOLD = 200;

/** The number of cases a figure was computed over, when the run recorded it. */
export function evidenceCases(evidence: KpiEvidence | null | undefined): number | null {
    const n = evidence?.sampleSize ?? evidence?.caseCount;
    return n === null || n === undefined || !Number.isFinite(Number(n)) ? null : Number(n);
}

/**
 * Whether a figure should be presented as indicative rather than as a result.
 *
 * No evidence recorded means nothing to judge by, so the figure is shown as
 * before rather than downgraded on a guess.
 */
export function isIndicative(evidence: KpiEvidence | null | undefined): boolean {
    if (!evidence) return false;
    if (evidence.confidence === 'low') return true;
    const n = evidenceCases(evidence);
    return n !== null && n < INDICATIVE_CASE_THRESHOLD;
}

/** e.g. "8 cases · low confidence". Null when the run recorded neither. */
export function evidenceLabel(evidence: KpiEvidence | null | undefined): string | null {
    const n = evidenceCases(evidence);
    const parts: string[] = [];
    if (n !== null) parts.push(`${count(n)} ${n === 1 ? 'case' : 'cases'}`);
    if (evidence?.confidence) parts.push(`${evidence.confidence} confidence`);
    return parts.length ? parts.join(' · ') : null;
}
